import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Store form data in localStorage (mock submission)
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    const newSubmission = {
      ...formData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    };
    submissions.push(newSubmission);
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));

    toast.success('Quote request sent successfully! We\'ll contact you within 24 hours.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: '',
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: [
        { label: 'Amrinder', value: '+1 437-248-1943', link: 'tel:+14372481943' },
      ],
    },
    {
      icon: Mail,
      title: 'Email',
      details: [
        { label: '', value: 'nteekrenos@gmail.com', link: 'mailto:nteekrenos@gmail.com' },
      ],
    },
    {
      icon: MapPin,
      title: 'Office Address',
      details: [
        { label: '', value: '5 Limestone Edge Lane', link: null },
        { label: '', value: 'Brampton, ON, L6X0H2', link: null },
      ],
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: [
        { label: 'Mon - Sat', value: '8:00 AM - 6:00 PM', link: null },
        { label: 'Sunday', value: 'By Appointment', link: null },
      ],
    },
  ];

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-1/3 h-1/2 bg-blue-50/30 rounded-full blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full border border-blue-100 mb-6">
            <span className="text-[#0070C0] font-semibold text-sm">GET IN TOUCH</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Get a free, no-obligation quote for your renovation project. Our team is ready to bring
            your vision to life.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-2 space-y-8">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#0070C0]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{info.title}</h3>
                      <div className="space-y-2">
                        {info.details.map((detail, idx) => (
                          <div key={idx}>
                            {detail.label && (
                              <span className="text-sm text-gray-500 block">{detail.label}</span>
                            )}
                            {detail.link ? (
                              <a
                                href={detail.link}
                                className="text-[#0070C0] hover:text-[#005A9C] font-semibold transition-colors"
                              >
                                {detail.value}
                              </a>
                            ) : (
                              <span className="text-gray-700 font-medium">{detail.value}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Location Note */}
            <div className="bg-gradient-to-br from-[#0070C0] to-[#005A9C] p-8 rounded-2xl text-white shadow-xl">
              <MapPin className="w-8 h-8 mb-4" />
              <h3 className="text-xl font-bold mb-2">Serving Ontario</h3>
              <p className="text-blue-50 leading-relaxed">
                We proudly serve residential and commercial clients across Ontario, bringing quality
                renovations to your doorstep.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Request a Free Quote</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (123) 456-7890"
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Interested In *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0070C0] focus:border-transparent transition-all"
                  >
                    <option value="">Select a service</option>
                    <option value="kitchen">Custom Kitchens & Cabinets</option>
                    <option value="basement">Legal Basements</option>
                    <option value="bathroom">Bathroom Renovations</option>
                    <option value="tvwall">TV Walls & Fireplace Units</option>
                    <option value="flooring">Flooring Services</option>
                    <option value="painting">Professional Painting</option>
                    <option value="wainscotting">Wainscotting</option>
                    <option value="architectural">Architectural Drawings</option>
                    <option value="complete">Complete Interior Renovation</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Details *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your renovation project..."
                    rows={5}
                    className="w-full resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-[#0070C0] hover:bg-[#005A9C] text-white font-semibold py-6 text-lg group shadow-lg hover:shadow-xl transition-all"
                >
                  Send Quote Request
                  <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  We typically respond within 24 hours during business days
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
